import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
} from "@mui/material";
import {
  CheckCircle,
  Delete,
  Edit,
  Check,
  Close,
  QrCodeScanner,
  DoneAll,
  ArrowBack,
} from "@mui/icons-material";
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import shipmentService from "../../services/shipmentService";
import PageHeader from "../../components/common/PageHeader";
import BarcodeScanner from "../../components/common/BarcodeScanner";
import { selectStatusOptions } from "../../redux/slices/statusDefinitionsSlice";

// A session's scanned list + target status is saved to localStorage as the
// admin scans, so an accidental refresh/navigation/tab close doesn't throw
// away boxes they already scanned — they're restored the next time this
// page (same in/out direction) loads. Expires after a while so a long-dead
// draft doesn't come back and get committed against statuses that have
// since moved on.
const DRAFT_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours
const draftKey = (mode) => `waypoint-admin-scan-draft-${mode}`;

const loadDraft = (mode) => {
  try {
    const raw = localStorage.getItem(draftKey(mode));
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft?.rows?.length || Date.now() - (draft.savedAt || 0) > DRAFT_TTL_MS) {
      localStorage.removeItem(draftKey(mode));
      return null;
    }
    return draft;
  } catch {
    return null;
  }
};

const saveDraft = (mode, rows, targetStatus) => {
  try {
    if (rows.length === 0) {
      localStorage.removeItem(draftKey(mode));
      return;
    }
    localStorage.setItem(draftKey(mode), JSON.stringify({ rows, targetStatus, savedAt: Date.now() }));
  } catch {
    // Storage full/unavailable — the session just isn't draft-recoverable
    // this time, not worth surfacing to the admin mid-scan.
  }
};

// Preferred default target status per scan direction — a preference only
// (statuses are admin-configurable, so the key may not exist on every
// install), with a graceful fallback to the first available status below.
const MODE_CONFIG = {
  in: {
    title: "Scan In — Cayman Warehouse",
    subtitle: "Scan a package's tracking barcode to confirm it's physically at the Cayman warehouse after customs clearance",
    defaultStatusKey: "processing_in_gcm",
  },
  out: {
    title: "Scan Out — Delivery",
    subtitle: "Scan a package's tracking barcode as it leaves the warehouse, scheduled for delivery",
    defaultStatusKey: "scheduled_for_delivery",
  },
};

// Warehouse barcode scan — used for BOTH scan-in (Cayman, post-customs) and
// scan-out (leaving for delivery). Same mechanism either way: scan
// accumulates a checklist client-side (with live per-scan feedback from the
// read-only lookup endpoint) and commits the whole batch in ONE call, to
// whichever status is selected, when the admin finishes the session — so,
// same as every other bulk action in this app, the affected customers get
// one summary email each, not one per scanned item. This is an OPTIONAL
// shortcut — admins can still use the existing bulk status update / dock
// receipt pages manually.
const AdminScan = () => {
  const { mode } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const itemStatusOptions = useSelector(selectStatusOptions("item"));

  const config = MODE_CONFIG[mode] || MODE_CONFIG.in;
  // Falls back the same way `config` does, so the logged direction always
  // matches whichever config (in/out) actually drove this session.
  const scanDirection = MODE_CONFIG[mode] ? mode : "in";

  const [targetStatus, setTargetStatus] = useState("");
  const [rows, setRows] = useState([]); // [{_rowId, trackingNumber, description, customerName, currentStatusLabel, eligible, status: 'ok'|'error'}]
  const [looking, setLooking] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [finishResult, setFinishResult] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  // Stable per-row id, independent of trackingNumber — rows can be edited
  // (tracking number changes) or freshly prepended by a live scan at any
  // moment, so identifying a row by array index or by trackingNumber isn't
  // safe once editing is possible.
  const nextRowIdRef = useRef(0);

  // Reset the session and re-default the target status whenever the mode
  // (in/out) changes — navigating between them shouldn't carry over a scan
  // list or status pick from the other direction. If a draft was saved for
  // THIS direction (an accidental refresh/navigation mid-session), restore
  // it instead of starting blank.
  useEffect(() => {
    setFinishResult(null);
    const draft = loadDraft(scanDirection);
    if (draft) {
      setRows(draft.rows);
      setTargetStatus(draft.targetStatus || "");
      nextRowIdRef.current = draft.rows.reduce((max, r) => Math.max(max, (r._rowId ?? -1) + 1), 0);
      enqueueSnackbar(`Restored ${draft.rows.length} scanned item(s) from your last session`, { variant: "info" });
    } else {
      setRows([]);
      setTargetStatus("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Keep the draft current as the admin scans — cheap enough to run on
  // every change (localStorage writes are synchronous but small here).
  useEffect(() => {
    saveDraft(scanDirection, rows, targetStatus);
  }, [scanDirection, rows, targetStatus]);

  useEffect(() => {
    if (targetStatus || itemStatusOptions.length === 0) return;
    const preferred = itemStatusOptions.find((s) => s.value === config.defaultStatusKey);
    setTargetStatus((preferred || itemStatusOptions[0]).value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemStatusOptions, mode]);

  // Kept in sync with state but read from inside handleDetect via ref, not
  // as useCallback deps — otherwise handleDetect (and therefore the
  // onDetect prop BarcodeScanner receives) would get a new identity on
  // every scan/status change, which re-triggers BarcodeScanner's camera
  // start/stop effect and made the live preview flash black on every scan.
  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);
  const targetStatusRef = useRef(targetStatus);
  useEffect(() => {
    targetStatusRef.current = targetStatus;
  }, [targetStatus]);
  // Tracking numbers with a lookup currently in flight — the dedupe check
  // below only knows about already-COMMITTED rows, but shipmentService.
  // lookupItem is an async network call. A box held in front of the camera
  // gets re-decoded every ~1.5s (BarcodeScanner's cooldown); if that lookup
  // is still pending when the same barcode is decoded again, `rows` doesn't
  // have it yet, so the dedupe check passes and a second lookup fires —
  // two rows for one package. This closes that window.
  const inFlightRef = useRef(new Set());

  const targetStatusLabel = itemStatusOptions.find((s) => s.value === targetStatus)?.label || targetStatus;
  const eligibleCount = rows.filter((r) => r.status === "ok" && r.eligible).length;

  // Changing the target status makes every already-scanned row's eligibility
  // stale (it was computed against the OLD target) — re-run the lookup for
  // each one against the new target rather than leaving stale labels/checks
  // on screen.
  const handleTargetStatusChange = async (newTargetStatus) => {
    setTargetStatus(newTargetStatus);
    const okRows = rows.filter((r) => r.status === "ok");
    if (okRows.length === 0) return;
    const refreshed = await Promise.all(
      okRows.map(async (r) => {
        try {
          const { data } = await shipmentService.lookupItem(r.trackingNumber, newTargetStatus);
          return { ...data.data, status: "ok", _rowId: r._rowId };
        } catch (err) {
          return { trackingNumber: r.trackingNumber, status: "error", message: err.response?.data?.message || "Item not found", _rowId: r._rowId };
        }
      }),
    );
    setRows((prev) => {
      const refreshedById = new Map(refreshed.map((r) => [r._rowId, r]));
      return prev.map((r) => refreshedById.get(r._rowId) || r);
    });
  };

  const handleDetect = useCallback(
    async (trackingNumber, source) => {
      // Immediate confirmation of what just happened — a barcode/QR read
      // vs the manual-entry fallback — before the lookup call even
      // resolves, so the admin gets feedback the instant they scan, not a
      // beat later.
      enqueueSnackbar(
        source === "manual"
          ? `Entered manually: ${trackingNumber}`
          : source === "device"
            ? `Scanned via hardware scanner: ${trackingNumber}`
            : `Scanned from barcode/QR: ${trackingNumber}`,
        { variant: "default" },
      );

      // Case-insensitive — the same box shouldn't create two rows just
      // because a re-scan (or a hardware scanner vs. manual entry) came
      // back with different letter casing than the first read.
      const trackingLower = trackingNumber.toLowerCase();
      if (
        rowsRef.current.some((r) => r.trackingNumber.toLowerCase() === trackingLower) ||
        inFlightRef.current.has(trackingLower)
      ) {
        enqueueSnackbar(`${trackingNumber} already scanned`, { variant: "info" });
        return;
      }
      inFlightRef.current.add(trackingLower);
      setLooking(true);
      const _rowId = nextRowIdRef.current++;
      try {
        const { data } = await shipmentService.lookupItem(trackingNumber, targetStatusRef.current);
        setRows((prev) => [{ ...data.data, status: "ok", _rowId }, ...prev]);
      } catch (err) {
        setRows((prev) => [
          {
            trackingNumber,
            status: "error",
            message: err.response?.data?.message || "Item not found",
            _rowId,
          },
          ...prev,
        ]);
      } finally {
        inFlightRef.current.delete(trackingLower);
        setLooking(false);
      }
    },
    [enqueueSnackbar],
  );

  // Manual-entry autocomplete — a camera/hardware scanner sometimes can't
  // read a damaged/dirty barcode at all, so the admin types part of the
  // tracking number and picks from what actually matches (server-scoped to
  // the realistic range for whichever direction this session is — Package
  // Received through Processing in GCM for scan-in, or Processing in GCM
  // through Scheduled for Delivery, including Ready for Pickup, for
  // scan-out).
  const handleSearchSuggestions = useCallback(async (q) => {
    try {
      const { data } = await shipmentService.searchItems(q, scanDirection);
      return data.data.items || [];
    } catch {
      return [];
    }
  }, [scanDirection]);

  const removeRow = (rowId) => {
    setRows((prev) => prev.filter((r) => r._rowId !== rowId));
  };

  const startEdit = (row) => {
    setEditingRowId(row._rowId);
    setEditValue(row.trackingNumber);
  };

  const cancelEdit = () => {
    setEditingRowId(null);
    setEditValue("");
  };

  // Re-runs the same lookup a fresh scan would — so a corrected tracking
  // number gets checked against the database again (right item? already at
  // this status? eligible for the selected target?) instead of just
  // renaming the row in place.
  const saveEdit = async (rowId) => {
    const newTrackingNumber = editValue.trim();
    if (!newTrackingNumber) return;

    const newTrackingLower = newTrackingNumber.toLowerCase();
    const current = rows.find((r) => r._rowId === rowId);
    if (current && newTrackingLower === current.trackingNumber.toLowerCase()) {
      cancelEdit();
      return;
    }
    if (rows.some((r) => r._rowId !== rowId && r.trackingNumber.toLowerCase() === newTrackingLower)) {
      enqueueSnackbar(`${newTrackingNumber} is already in this session's list`, { variant: "warning" });
      return;
    }

    setEditSaving(true);
    try {
      const { data } = await shipmentService.lookupItem(newTrackingNumber, targetStatusRef.current);
      setRows((prev) => prev.map((r) => (r._rowId === rowId ? { ...data.data, status: "ok", _rowId: rowId } : r)));
    } catch (err) {
      setRows((prev) =>
        prev.map((r) =>
          r._rowId === rowId
            ? { trackingNumber: newTrackingNumber, status: "error", message: err.response?.data?.message || "Item not found", _rowId: rowId }
            : r,
        ),
      );
    } finally {
      setEditSaving(false);
      cancelEdit();
    }
  };

  const handleFinish = async () => {
    const trackingNumbers = rows.filter((r) => r.status === "ok" && r.eligible).map((r) => r.trackingNumber);
    if (trackingNumbers.length === 0 || !targetStatus) return;

    setFinishing(true);
    try {
      const { data } = await shipmentService.bulkUpdateTrackingStatus(
        trackingNumbers,
        targetStatus,
        undefined,
        scanDirection,
      );
      setFinishResult(data.data);
      enqueueSnackbar(data.message, { variant: "success" });
      setRows([]);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to update items", { variant: "error" });
    } finally {
      setFinishing(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Button startIcon={<ArrowBack />} size="small" onClick={() => navigate("/admin/scan")} sx={{ alignSelf: "flex-start" }}>
        Back
      </Button>

      <PageHeader title={config.title} subtitle={config.subtitle} />

      <Card>
        <CardContent>
          <TextField
            select
            size="small"
            label="Assign status to scanned items"
            value={targetStatus}
            onChange={(e) => handleTargetStatusChange(e.target.value)}
            sx={{ mb: 2.5, minWidth: 260 }}
          >
            {itemStatusOptions.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>

          <BarcodeScanner
            active
            onDetect={handleDetect}
            onSearchSuggestions={handleSearchSuggestions}
            hint="Scan the tracking barcode on the box — or switch to Camera above if a hardware scanner isn't handy"
          />
        </CardContent>
      </Card>

      {finishResult && (
        <Alert severity="success" onClose={() => setFinishResult(null)} sx={{ borderRadius: 2 }}>
          Marked {finishResult.updatedCount} item(s) as {targetStatusLabel}.
        </Alert>
      )}

      <Card>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: rows.length ? "1px solid" : "none",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <QrCodeScanner fontSize="small" />
            Scanned this session ({rows.length})
            {looking && <CircularProgress size={14} />}
          </Typography>
          <Button
            variant="contained"
            startIcon={finishing ? <CircularProgress size={18} color="inherit" /> : <DoneAll />}
            disabled={finishing || eligibleCount === 0 || !targetStatus}
            onClick={handleFinish}
          >
            {finishing ? "Updating…" : `Finish Session — Mark ${eligibleCount || ""} ${targetStatusLabel}`}
          </Button>
        </Box>

        {rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No items scanned yet — scan a box's tracking barcode to get started.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {rows.map((r) => {
              const isEditing = editingRowId === r._rowId;
              return (
                <ListItem
                  key={r._rowId}
                  divider
                  secondaryAction={
                    isEditing ? (
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton
                          edge="end"
                          size="small"
                          color="primary"
                          disabled={editSaving}
                          onClick={() => saveEdit(r._rowId)}
                        >
                          {editSaving ? <CircularProgress size={16} /> : <Check fontSize="small" />}
                        </IconButton>
                        <IconButton edge="end" size="small" disabled={editSaving} onClick={cancelEdit}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton edge="end" size="small" onClick={() => startEdit(r)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton edge="end" size="small" onClick={() => removeRow(r._rowId)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    )
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {r.status === "ok" ? (
                      <CheckCircle color={r.eligible ? "success" : "disabled"} fontSize="small" />
                    ) : (
                      <ErrorOutlineOutlinedIcon color="error" fontSize="small" />
                    )}
                  </ListItemIcon>
                  {isEditing ? (
                    <TextField
                      size="small"
                      autoFocus
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit(r._rowId);
                        if (e.key === "Escape") cancelEdit();
                      }}
                      sx={{ mr: 8, flex: 1 }}
                    />
                  ) : r.status === "ok" ? (
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Typography variant="body2" fontWeight={600}>
                            {r.description}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              !r.eligible
                                ? `Already ${r.currentStatusLabel}`
                                : r.currentStatus === targetStatus
                                  ? `Re-mark ${r.targetStatusLabel}`
                                  : `→ ${r.targetStatusLabel}`
                            }
                            color={r.eligible ? "primary" : "default"}
                            variant="outlined"
                          />
                          {r.matchCount > 1 && (
                            <Chip
                              size="small"
                              label={`${r.matchCount} items across ${r.shipmentCount} shipments`}
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {r.trackingNumber} · {r.customerName} · Shipment {r.shipmentTrackingNumber} · Currently{" "}
                          {r.currentStatusLabel}
                          {r.matchCount > 1 ? " · this tracking # is shared — finishing updates all of them" : ""}
                        </Typography>
                      }
                    />
                  ) : (
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600} color="error.main">
                          {r.trackingNumber}
                        </Typography>
                      }
                      secondary={r.message}
                    />
                  )}
                </ListItem>
              );
            })}
          </List>
        )}
      </Card>
    </Box>
  );
};

export default AdminScan;
